<template>
  <section class="py-16 px-6 md:px-12 bg-white">
    <div class="max-w-[1600px] mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div
          v-for="metric in coreMetrics"
          :key="metric.key"
          class="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-10 md:p-12 text-center shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300"
        >
          <i :class="['fas', metric.icon, 'text-5xl', 'mb-4', metric.color]"></i>
          <div class="text-5xl font-extrabold text-gray-900 mb-2">
            <span ref="countersRef">{{ metric.value }}</span>
            <span v-if="metric.unit" class="text-3xl">{{ metric.unit }}</span>
          </div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">
            {{ getLocalizedText(metric.label) }}
          </h3>
          <p class="text-gray-600 text-sm">
            {{ getLocalizedText(metric.description) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import coreMetricsData from '@/data/coreMetrics';

const { locale } = useI18n();
const coreMetrics = ref(coreMetricsData);
const countersRef = ref([]);

// 获取本地化文本
const getLocalizedText = (textObj) => {
  if (!textObj) return '';
  return textObj[locale.value] || textObj.zh || textObj.en || '';
};

// 数字滚动动画
const animateCounter = (element, start, end, duration, hasDecimal = false) => {
  const startTime = performance.now();
  const isDecimal = hasDecimal || end.toString().includes('.');
  
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // 使用 easeOutQuart 缓动函数
    const easeProgress = 1 - Math.pow(1 - progress, 4);
    const current = start + (end - start) * easeProgress;
    
    if (isDecimal) {
      element.textContent = current.toFixed(2);
    } else {
      element.textContent = Math.floor(current);
    }
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.textContent = isDecimal ? end.toFixed(2) : end;
    }
  };
  
  requestAnimationFrame(animate);
};

// Intersection Observer - 当元素进入视口时触发动画
const setupObserver = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting && !entry.target.dataset.animated) {
          const metric = coreMetrics.value[index];
          const endValue = parseFloat(metric.value);
          const hasDecimal = metric.value.includes('.');
          
          setTimeout(() => {
            animateCounter(entry.target, 0, endValue, 2000, hasDecimal);
            entry.target.dataset.animated = 'true';
          }, index * 100); // 错开动画时间
        }
      });
    },
    { threshold: 0.5 }
  );
  
  countersRef.value.forEach((el) => {
    if (el) observer.observe(el);
  });
};

onMounted(() => {
  setTimeout(setupObserver, 100);
});
</script>

<style scoped>
/* 渐变动画 */
@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.bg-gradient-to-br {
  background-size: 200% 200%;
  animation: gradientShift 10s ease infinite;
}
</style>

